import { useEffect, useState } from 'react';
import { cn } from '@waymail/ui';
import type { Conta, EstadoDaSincronizacao } from '../lib/api';
import { useContas } from '../lib/consultas';
import { useOnline } from '../lib/rede';

/**
 * Estado da sincronizacao de cada caixa, na secao Contas da barra lateral
 * (ADR 0053). Pequeno e sem bloquear nada: um ponto e uma frase.
 *
 * Nunca mostra o `lastError`. Ele e texto do servidor da caixa e pode citar
 * comando, host ou caixa; o que a pessoa precisa saber cabe nos quatro
 * estados, e o que fazer cabe na dica.
 */

interface Rotulo {
  texto: string;
  dica: string;
  tom: 'ok' | 'andamento' | 'alerta' | 'erro';
}

const TONS: Record<Rotulo['tom'], string> = {
  ok: 'bg-success',
  andamento: 'bg-rail animate-pulse motion-reduce:animate-none',
  alerta: 'bg-warning',
  erro: 'bg-error',
};

/** O que a tela mostra para a conta, agora. Exportado para o teste. */
export function rotuloDoEstado(conta: Conta, online: boolean, agora = Date.now()): Rotulo {
  if (!online) {
    return {
      texto: 'Sem conexão',
      dica: 'Este computador está sem internet. O que chegar aparece quando a rede voltar.',
      tom: 'alerta',
    };
  }

  const estado: EstadoDaSincronizacao =
    conta.syncStatus ??
    (conta.syncState === 'syncing'
      ? 'sincronizando'
      : conta.syncState === 'error'
        ? 'sem_conexao'
        : conta.lastSyncAt === null
          ? 'sincronizando'
          : 'em_dia');

  if (estado === 'credencial') {
    return {
      texto: 'Erro de credencial',
      dica: 'O servidor da caixa recusou a senha. Reconecte a caixa com a senha atual.',
      tom: 'erro',
    };
  }
  if (estado === 'sem_conexao') {
    return {
      texto: 'Sem conexão',
      dica: 'Não foi possível falar com o servidor da caixa. Tentaremos de novo sozinhos.',
      tom: 'alerta',
    };
  }
  if (estado === 'sincronizando') {
    // Nunca sincronizou (ADR 0056): a Entrada vazia ainda nao quer dizer nada.
    return conta.lastSyncAt === null
      ? {
          texto: 'Sincronizando a primeira vez…',
          dica: 'Trazendo as mensagens desta caixa pela primeira vez.',
          tom: 'andamento',
        }
      : { texto: 'Sincronizando', dica: 'Buscando o que chegou.', tom: 'andamento' };
  }

  return { texto: desde(conta.lastSyncAt, agora), dica: 'Caixa em dia.', tom: 'ok' };
}

function desde(iso: string | null, agora: number): string {
  if (iso === null) return 'Sincronizado';
  const ms = agora - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 2 * 60_000) return 'Sincronizado agora';
  const minutos = Math.floor(ms / 60_000);
  if (minutos < 60) return `Sincronizado há ${minutos} min`;
  const hora = new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `Sincronizado às ${hora}`;
}

export function EstadoDasContas() {
  const contas = useContas();
  const online = useOnline();
  // "Ha 3 min" precisa andar sozinho, sem esperar outro evento.
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  if (contas.data === undefined || contas.data.length === 0) return null;

  return (
    <ul aria-label="Estado das caixas" className="mb-1 flex flex-col gap-0.5">
      {contas.data.map((conta) => {
        const r = rotuloDoEstado(conta, online, agora);
        return (
          <li key={conta.id} title={r.dica} className="flex min-w-0 flex-col px-2.5 py-1.5">
            <span className="truncate text-[13px] font-medium text-primary">{conta.email}</span>
            <span className="flex items-center gap-1.5 text-[12px] text-tertiary">
              <span
                aria-hidden="true"
                className={cn('size-1.5 shrink-0 rounded-full', TONS[r.tom])}
              />
              {r.texto}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
